import { isResolvedAck, findingEvidenceKey } from '../lib/findingAck';
import React, { useState } from 'react';
import { reportForPresentation } from '../lib/scoring';
import { t } from '../lib/i18n';
import Icon from '../components/Icon';
import IssueCard from './IssueCard';
import { getIssueTitle } from './issueText';

/**
 * TIER_COLORS — Tailwind class maps for priority tiers.
 * Full class names required (no dynamic interpolation) for Tailwind CDN purge.
 */
const TIER_COLORS = {
    critical: {
        card: 'bg-red-50 dark:bg-red-900/20 border-red-300 dark:border-red-700',
        badge: 'bg-red-500 text-white',
        text: 'text-red-800 dark:text-red-200',
        icon: 'text-red-500',
    },
    check: {
        card: 'bg-amber-50 dark:bg-amber-900/20 border-amber-300 dark:border-amber-700',
        badge: 'bg-amber-500 text-white',
        text: 'text-amber-800 dark:text-amber-200',
        icon: 'text-amber-500',
    },
    info: {
        card: 'bg-blue-50 dark:bg-blue-900/20 border-blue-300 dark:border-blue-700',
        badge: 'bg-blue-500 text-white',
        text: 'text-blue-800 dark:text-blue-200',
        icon: 'text-blue-500',
    },
    passed: {
        card: 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-300 dark:border-emerald-700',
        badge: 'bg-emerald-500 text-white',
        text: 'text-emerald-800 dark:text-emerald-200',
        icon: 'text-emerald-500',
    },
};

/**
 * PriorityGroupedList — Groups audit findings into three tiers:
 *   1. Critical (errors) — must fix
 *   2. Check (warnings) — should review
 *   3. Passed (info/success) — collapsible, shown as pills when collapsed
 *
 * Extracted from index.html (previously inline ~lines 1126-1201).
 *
 * @param {object} props
 *   - report: { issues: Array }
 *   - globalExpand: boolean | null
 *   - getAck: (ruleId) => ackStatus | null
 *   - onAck: (ruleId, action) => void
 *   - onUndoAck: (ackId) => void
 *   - clientId: string
 */
export default function PriorityGroupedList({ report: storedReport, globalExpand, getAck, onAck, onUndoAck, clientId: listClientId, period }) {

    const report = reportForPresentation(storedReport);
    const [passedOpen, setPassedOpen] = useState(false);
    const issues = (report?.issues || []).filter(i => !['financial_result', 'scoring', 'health'].includes(i.specialType));
    const resolved = issues.filter(i => isResolvedAck(getAck?.(i.ruleId, i)));
    const active = issues.filter(i => !resolved.includes(i));
    const critical = active.filter(i => i.type === 'error');
    const check = active.filter(i => i.type === 'warning' || i.specialType === 'review_signal');
    const info = active.filter(i => i.type === 'info' && i.specialType !== 'review_signal');
    const passed = issues.filter(i => i.type === 'success');
    const issueKey = (tier, issue, idx) => [
        listClientId || '', period?.year || '', period?.monthFrom || '', period?.monthTo || '',
        findingEvidenceKey(issue, period) || '',
        tier,
        issue.ruleId || issue.specialType || 'issue',
        issue.details?.[0]?.accNum ?? '',
        issue.details?.[0]?.name ?? '',
        idx,
    ].join(':');

    const TierHeader = ({ color, icon, label, count }) => (
        <div className={`flex items-center gap-3 mb-3 px-1`}>
            <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full ${TIER_COLORS[color].badge}`}>
                <Icon name={icon} className="w-4 h-4" />
            </span>
            <h3 className={`font-bold font-heading text-lg ${TIER_COLORS[color].text}`}>{label}</h3>
            <span className={`ml-auto text-sm font-mono font-bold ${TIER_COLORS[color].text}`}>{count}</span>
        </div>
    );

    return (
        <div className="priority-group space-y-6 mb-8">
            {/* CRITICAL — Må rettes */}
            {critical.length > 0 && (
                <div>
                    <TierHeader color="critical" icon="alert-octagon" label={t('tier_critical')} count={critical.length} />
                    <div className="space-y-3">
                        {critical.map((issue, idx) => <IssueCard key={issueKey('crit', issue, idx)} issue={issue} globalExpand={globalExpand} ackStatus={getAck && getAck(issue.ruleId, issue)} onAck={onAck} onUndoAck={onUndoAck} clientId={listClientId} />)}
                    </div>
                </div>
            )}

            {/* CHECK — Bør sjekkes */}
            {check.length > 0 && (
                <div>
                    <TierHeader color="check" icon="alert-triangle" label={t('tier_check')} count={check.length} />
                    <div className="space-y-3">
                        {check.map((issue, idx) => <IssueCard key={issueKey('chk', issue, idx)} issue={issue} globalExpand={globalExpand} ackStatus={getAck && getAck(issue.ruleId, issue)} onAck={onAck} onUndoAck={onUndoAck} clientId={listClientId} />)}
                    </div>
                </div>
            )}

            {/* INFO — Til orientering / Do wiadomości */}
            {info.length > 0 && (
                <div className="mb-4">
                    <TierHeader color="info" icon="info" label={t('tier_info') || 'Info'} count={info.length} />
                    <div className="space-y-3">
                        {info.map((issue, idx) => <IssueCard key={issueKey('info', issue, idx)} issue={issue} globalExpand={globalExpand} ackStatus={getAck && getAck(issue.ruleId, issue)} onAck={onAck} onUndoAck={onUndoAck} clientId={listClientId} />)}
                    </div>
                </div>
            )}

            {/* PASSED — OK / Brak odchyleń */}
            {passed.length > 0 && (
                <div>
                    <button onClick={() => setPassedOpen(!passedOpen)} className="flex items-center gap-3 mb-3 px-1 w-full text-left group">
                        <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full ${TIER_COLORS.passed.badge}`}>
                            <Icon name="check-circle-2" className="w-4 h-4" />
                        </span>
                        <h3 className={`font-bold font-heading text-lg ${TIER_COLORS.passed.text}`}>{t('tier_passed')}</h3>
                        <span className={`text-sm font-mono font-bold ${TIER_COLORS.passed.text}`}>{passed.length}</span>
                        <Icon name={passedOpen ? "chevron-up" : "chevron-down"} className="w-4 h-4 ml-auto text-slate-400 group-hover:text-slate-600 dark:group-hover:text-slate-200 transition" />
                    </button>
                    {!passedOpen && (
                        <div className="flex flex-wrap gap-1.5 px-1 mb-3">
                            {passed.map((issue, idx) => (
                                <span key={issueKey('pt', issue, idx)} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800">
                                    <Icon name="check" className="w-3 h-3" /> {getIssueTitle(issue)}
                                </span>
                            ))}
                        </div>
                    )}
                    {passedOpen && (
                        <div className="space-y-3">
                            {passed.map((issue, idx) => <IssueCard key={issueKey('pass', issue, idx)} issue={issue} globalExpand={globalExpand} ackStatus={getAck && getAck(issue.ruleId, issue)} onAck={onAck} onUndoAck={onUndoAck} clientId={listClientId} />)}
                        </div>
                    )}
                </div>
            )}

            {resolved.length > 0 && <div>
                <TierHeader color="info" icon="check" label={t('tier_resolved')} count={resolved.length} />
                {resolved.map((issue, idx) => <IssueCard key={issueKey('resolved', issue, idx)} issue={issue} globalExpand={globalExpand} ackStatus={getAck(issue.ruleId, issue)} onAck={onAck} onUndoAck={onUndoAck} clientId={listClientId} />)}
            </div>}
            {/* Empty state */}
            {issues.length === 0 && (
                <div className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-xl p-10 text-center">
                    <Icon name="check-circle" className="w-16 h-16 text-emerald-600 mx-auto mb-4" />
                    <h2 className="text-xl font-bold text-emerald-800 dark:text-emerald-400">{t('noErrors')}</h2>
                </div>
            )}
        </div>
    );
}
